import type { Currency, DiscountType, QuoteTotals } from './types.js';
import { CURRENCY_SYMBOLS } from './constants.js';
import { currencyDigits, roundCurrency } from './currencies.js';

/**
 * Money is stored and computed in minor units conceptually, but persisted as a
 * plain number rounded to the currency precision. Monetary boundaries use that precision so
 * the API, the PDF and the UI can never disagree about a total.
 */
export function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export interface TotalsInput {
  currency?: Currency;
  items: Array<{ quantity: number; unitPrice: number }>;
  discountType: DiscountType;
  discountValue: number;
  /**
   * VAT percentage applied to the discounted base. 0 is a legitimate value —
   * an עוסק פטור charges none — and is also what every quote issued before VAT
   * support carries, so their stored totals keep adding up.
   */
  vatRate?: number;
  /**
   * Whether the prices typed into the line items already contain VAT. B2C
   * businesses quote the payable figure and work backwards to the taxable
   * base; B2B quote net and add VAT on top (the default).
   */
  pricesIncludeVat?: boolean;
}

/**
 * Single implementation of quote arithmetic, shared by the API (authoritative)
 * and the web editor (live preview). Discount never pushes the total below zero.
 *
 * `net` is always the taxable base and `total` always the payable figure, so
 * `net + vat === total` holds in both VAT modes and the PDF can print the three
 * lines an Israeli quote is expected to show.
 */
export function computeTotals({
  items,
  discountType,
  discountValue,
  vatRate = 0,
  pricesIncludeVat = false,
  currency = 'ILS',
}: TotalsInput): QuoteTotals {
  const round = (value: number) => roundCurrency(value, currency);
  const subtotal = round(items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0));

  let discount = 0;
  if (discountType === 'amount') discount = round(Math.max(0, discountValue));
  else if (discountType === 'percent') discount = round((subtotal * Math.max(0, discountValue)) / 100);

  discount = Math.min(discount, subtotal);
  const base = round(subtotal - discount);
  const rate = Math.max(0, vatRate);

  if (rate === 0) return { subtotal, discount, net: base, vatRate: 0, vat: 0, total: base };

  if (pricesIncludeVat) {
    // The base is the payable figure; peel the tax back out of it.
    const net = round(base / (1 + rate / 100));
    return { subtotal, discount, net, vatRate: rate, vat: round(base - net), total: base };
  }

  const vat = round((base * rate) / 100);
  return { subtotal, discount, net: base, vatRate: rate, vat, total: round(base + vat) };
}

/** 18 → "18", 17.5 → "17.5". A trailing ".00" inside a tax label reads as noise. */
export function formatRate(rate: number): string {
  return String(round2(rate));
}

export function lineTotal(quantity: number, unitPrice: number, currency: Currency = 'ILS'): number {
  return roundCurrency(quantity * unitPrice, currency);
}

/**
 * Locale-aware currency formatting. Hebrew uses the `he-IL` number system;
 * the symbol placement is handled by Intl, not by string concatenation.
 */
export function formatMoney(amount: number, currency: Currency, locale = 'he-IL'): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: currencyDigits(currency),
      maximumFractionDigits: currencyDigits(currency),
    }).format(amount);
  } catch {
    return `${CURRENCY_SYMBOLS[currency]}${amount.toFixed(currencyDigits(currency))}`;
  }
}
